import React, { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { SlideOver } from "@/components/SlideOver";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CARRIERS } from "@/lib/constants";
import { api, apiError } from "@/lib/api";

const selectClass = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const textareaClass = "flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function ShipmentForm({ clients, availableProducts, onSaved }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const empty = {
    client_id: clients[0]?.id || "", product_id: "",
    store_name: "", store_address: "", city: "", state: "", zip: "",
    carrier: CARRIERS[0], notes: "",
  };
  const [v, setV] = useState(empty);
  const clientProducts = availableProducts.filter((p) => p.client_id === v.client_id);

  function set(k, val) { setV((p) => ({ ...p, [k]: val })); }

  async function submit(e) {
    e.preventDefault();
    setError(null); setSaving(true);
    try {
      await api.post("/shipments", v);
      setOpen(false); setV(empty);
      onSaved && onSaved();
    } catch (err) { setError(apiError(err)); } finally { setSaving(false); }
  }

  return (
    <>
      <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Create Shipment</Button>
      <SlideOver open={open} onClose={() => setOpen(false)} title="Create Shipment">
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
          <div className="space-y-2">
            <Label htmlFor="client_id">Organization *</Label>
            <select id="client_id" className={selectClass} value={v.client_id} onChange={(e) => { set("client_id", e.target.value); set("product_id", ""); }} required>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="product_id">Product *</Label>
            <select id="product_id" className={selectClass} value={v.product_id} onChange={(e) => set("product_id", e.target.value)} required>
              <option value="">{clientProducts.length === 0 ? "No available products for this client" : "Select a product..."}</option>
              {clientProducts.map((p) => <option key={p.id} value={p.id}>{p.serial_number} ({p.product_type})</option>)}
            </select>
            <p className="text-xs text-muted-foreground">Only products currently available in the warehouse are listed.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="store_name">Store Name *</Label>
            <Input id="store_name" value={v.store_name} onChange={(e) => set("store_name", e.target.value)} placeholder="e.g. T-Mobile Little Rock" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="store_address">Street Address *</Label>
            <Input id="store_address" value={v.store_address} onChange={(e) => set("store_address", e.target.value)} required />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1 space-y-2">
              <Label htmlFor="city">City *</Label>
              <Input id="city" value={v.city} onChange={(e) => set("city", e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State *</Label>
              <Input id="state" value={v.state} onChange={(e) => set("state", e.target.value)} placeholder="AR" maxLength={2} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zip">ZIP *</Label>
              <Input id="zip" value={v.zip} onChange={(e) => set("zip", e.target.value)} required />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="carrier">Carrier</Label>
            <select id="carrier" className={selectClass} value={v.carrier} onChange={(e) => set("carrier", e.target.value)}>
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <textarea id="notes" rows={2} value={v.notes} onChange={(e) => set("notes", e.target.value)} className={textareaClass} />
          </div>
          <Button type="submit" className="w-full" disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Shipment
          </Button>
        </form>
      </SlideOver>
    </>
  );
}
